import { useCallback, useEffect, useRef, useState, type ChangeEvent } from "react";
import { Link } from "react-router";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { ArrowUp, Gem, ImageIcon, Mic, Music2, Paperclip, X } from "lucide-react";
import { hive } from "../../lib/apiClient";
import { muhahaNowStartHref, type MuhahaNowAttachment } from "../../lib/muhahaNow";
import { errorMessage } from "../../lib/format";
import { track } from "../../lib/analytics";
import { useStore } from "../../stores/rootStore";
import { Spinner } from "../ui/Spinner";

type CreateAttachmentKind = "image" | "audio";

interface CreateAttachment {
	id: string;
	kind: CreateAttachmentKind;
	file: File;
	previewUrl?: string;
}

interface UploadedAttachments {
	items: MuhahaNowAttachment[];
	assetIds: string[];
}

const MAX_ATTACHMENTS = 4;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

function fileKey(file: File): string {
	return `${file.name}:${file.size}:${file.lastModified}:${file.type}`;
}

async function deleteUploadedAssets(ids: string[]): Promise<void> {
	await Promise.allSettled(ids.map((id) => hive.me.deleteAsset(id)));
}

async function uploadAttachments(items: CreateAttachment[]): Promise<UploadedAttachments> {
	const uploadedIds: string[] = [];
	const attachments: MuhahaNowAttachment[] = [];
	try {
		for (const item of items) {
			const uploaded = await hive.uploads.uploadDirect({
				name: item.file.name || `attachment-${item.kind}`,
				mime: item.file.type,
				bytes: item.file.size,
				data: item.file,
			});
			uploadedIds.push(uploaded.id);
			attachments.push({ url: uploaded.url, mime: uploaded.mime });
		}
		return { items: attachments, assetIds: uploadedIds };
	} catch (error) {
		await deleteUploadedAssets(uploadedIds);
		throw error;
	}
}

export const CreateComposer = observer(
	({
		variant = "home",
		remixOf,
		remixSource,
		placeholder,
		autoFocus = false,
		embedded = false,
	}: {
		variant?: "home" | "page";
		remixOf?: string;
		remixSource?: { title?: string | null } | null;
		placeholder?: string;
		autoFocus?: boolean;
		embedded?: boolean;
	}) => {
		const { auth, wallet, ui } = useStore();
		const { t } = useTranslation();
		const isHome = variant === "home";
		const promptPlaceholder = placeholder ?? t("createGame.promptPlaceholder");
		const [prompt, setPrompt] = useState("");
		const [typedPlaceholder, setTypedPlaceholder] = useState(isHome ? "" : promptPlaceholder);
		const [attachments, setAttachments] = useState<CreateAttachment[]>([]);
		const [attachmentMenuOpen, setAttachmentMenuOpen] = useState(false);
		const [submitting, setSubmitting] = useState(false);
		const [error, setError] = useState("");
		const imageInputRef = useRef<HTMLInputElement>(null);
		const audioInputRef = useRef<HTMLInputElement>(null);
		const attachmentMenuRef = useRef<HTMLDivElement>(null);
		const previewUrlsRef = useRef(new Set<string>());
		const submittingRef = useRef(false);

		useEffect(() => {
			if (!isHome || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
				setTypedPlaceholder(promptPlaceholder);
				return;
			}

			let visibleCharacters = 0;
			setTypedPlaceholder("");
			const timer = window.setInterval(() => {
				visibleCharacters += 1;
				setTypedPlaceholder(promptPlaceholder.slice(0, visibleCharacters));
				if (visibleCharacters >= promptPlaceholder.length) window.clearInterval(timer);
			}, 42);
			return () => window.clearInterval(timer);
		}, [isHome, promptPlaceholder]);

		useEffect(
			() => () => {
				for (const url of previewUrlsRef.current) URL.revokeObjectURL(url);
				previewUrlsRef.current.clear();
			},
			[],
		);

		useEffect(() => {
			if (!attachmentMenuOpen) return;

			function handlePointerDown(event: PointerEvent) {
				if (!attachmentMenuRef.current?.contains(event.target as Node)) {
					setAttachmentMenuOpen(false);
				}
			}

			function handleKeyDown(event: KeyboardEvent) {
				if (event.key === "Escape") setAttachmentMenuOpen(false);
			}

			document.addEventListener("pointerdown", handlePointerDown);
			document.addEventListener("keydown", handleKeyDown);
			return () => {
				document.removeEventListener("pointerdown", handlePointerDown);
				document.removeEventListener("keydown", handleKeyDown);
			};
		}, [attachmentMenuOpen]);

		const addAttachments = useCallback(
			(files: File[], kind: CreateAttachmentKind) => {
				const existing = new Set(attachments.map((item) => fileKey(item.file)));
				const additions: CreateAttachment[] = [];
				let invalidType = false;
				let tooLarge = false;
				let limitReached = false;

				for (const file of files) {
					if (attachments.length + additions.length >= MAX_ATTACHMENTS) {
						limitReached = true;
						break;
					}
					if (!file.type.startsWith(`${kind}/`)) {
						invalidType = true;
						continue;
					}
					if (file.size > MAX_ATTACHMENT_BYTES) {
						tooLarge = true;
						continue;
					}
					const key = fileKey(file);
					if (existing.has(key)) continue;
					existing.add(key);
					const previewUrl = kind === "image" ? URL.createObjectURL(file) : undefined;
					if (previewUrl) previewUrlsRef.current.add(previewUrl);
					additions.push({ id: crypto.randomUUID(), kind, file, previewUrl });
				}

				if (invalidType) ui.toast(t("createGame.attachmentInvalidType"), "error");
				if (tooLarge) ui.toast(t("createGame.attachmentTooLarge"), "error");
				if (limitReached) ui.toast(t("createGame.attachmentLimit", { count: MAX_ATTACHMENTS }), "info");
				if (additions.length) {
					setError("");
					setAttachments([...attachments, ...additions]);
				}
			},
			[attachments, t, ui],
		);

		function handleAttachmentChange(event: ChangeEvent<HTMLInputElement>, kind: CreateAttachmentKind) {
			const input = event.currentTarget;
			const files = Array.from(input.files ?? []);
			input.value = "";
			addAttachments(files, kind);
		}

		function removeAttachment(id: string) {
			setAttachments((current) => {
				const removed = current.find((item) => item.id === id);
				if (removed?.previewUrl) {
					URL.revokeObjectURL(removed.previewUrl);
					previewUrlsRef.current.delete(removed.previewUrl);
				}
				return current.filter((item) => item.id !== id);
			});
		}

		async function submit() {
			const trimmed = prompt.trim();
			if (!trimmed || submittingRef.current) return;
			if (!auth.isLoggedIn) {
				const signedIn = await ui.requestAuth();
				if (!signedIn) return;
			}
			if (!wallet.loaded) await wallet.load();
			if (wallet.freeRemaining <= 0 && wallet.creditsBalance <= 0) {
				setError(t("createGame.notEnoughCredits"));
				return;
			}

			submittingRef.current = true;
			setSubmitting(true);
			setError("");
			let uploadedAssetIds: string[] = [];
			track("creation_started", {
				content_kind: "game",
				mode: remixOf ? "remix" : "quick",
			});
			try {
				const uploaded = await uploadAttachments(attachments);
				uploadedAssetIds = uploaded.assetIds;
				const href = muhahaNowStartHref({
					prompt: trimmed,
					attachments: uploaded.items,
					remixPlayableId: remixOf,
					remixTitle: remixSource?.title ?? undefined,
				});
				if (!href) throw new Error(t("createGame.handoffUnavailable"));
				uploadedAssetIds = [];
				window.location.assign(href);
			} catch (err) {
				await deleteUploadedAssets(uploadedAssetIds);
				setError(errorMessage(err, t("createGame.startFailed")));
				submittingRef.current = false;
				setSubmitting(false);
			}
		}

		const canGenerate = !wallet.loaded || wallet.freeRemaining > 0 || wallet.creditsBalance > 0;
		const submitReady = Boolean(prompt.trim()) && canGenerate;
		return (
			<div className="create-composer w-full">
				<div
					className={`create-composer-panel transition ${
						embedded
							? "border-0 bg-transparent p-3 shadow-none"
							: `border border-white/20 bg-[#19151f]/96 shadow-[0_24px_70px_rgba(0,0,0,0.48)] backdrop-blur-sm focus-within:border-white/35 ${
									isHome ? "rounded-[24px] p-3" : "rounded-2xl p-4"
								}`
					}`}
				>
					<input
						ref={imageInputRef}
						type="file"
						accept="image/*"
						multiple
						disabled={submitting}
						onChange={(event) => handleAttachmentChange(event, "image")}
						className="hidden"
					/>
					<input
						ref={audioInputRef}
						type="file"
						accept="audio/*"
						multiple
						disabled={submitting}
						onChange={(event) => handleAttachmentChange(event, "audio")}
						className="hidden"
					/>
					<textarea
						value={prompt}
						onChange={(event) => setPrompt(event.target.value)}
						autoFocus={autoFocus}
						onKeyDown={(event) => {
							if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
							event.preventDefault();
							void submit();
						}}
						placeholder={isHome ? typedPlaceholder : promptPlaceholder}
						aria-label={promptPlaceholder}
						maxLength={1000}
						rows={isHome ? 3 : 7}
						className={`create-composer-input w-full resize-none border-0 bg-transparent px-2 py-2 text-white outline-none placeholder:text-white/35 ${
							isHome ? "min-h-24 text-base" : "min-h-48 text-lg"
						}`}
					/>

					{attachments.length ? (
						<div className="flex flex-wrap gap-2 px-2 pb-3" aria-label={t("createGame.attachmentsLabel")}>
							{attachments.map((attachment) => (
								<div
									key={attachment.id}
									className="create-composer-attachment relative flex h-14 max-w-56 items-center gap-2 overflow-hidden rounded-md border border-white/10 bg-white/6 px-2.5"
								>
									{attachment.previewUrl ? (
										<img src={attachment.previewUrl} alt="" className="size-10 rounded object-cover" />
									) : (
										<Music2 size={18} className="shrink-0 text-violet" />
									)}
										<span className="create-composer-attachment-name min-w-0 flex-1 truncate text-xs font-semibold text-white/70">
										{attachment.file.name}
									</span>
									<button
										type="button"
										onClick={() => removeAttachment(attachment.id)}
										className="create-composer-remove grid size-7 shrink-0 place-items-center rounded-full text-white/45 transition hover:bg-white/10 hover:text-white"
										aria-label={t("createGame.removeAttachment", { name: attachment.file.name })}
									>
										<X size={14} />
									</button>
								</div>
							))}
						</div>
					) : null}

					<div className="flex items-center justify-end gap-2 px-1 pt-2">
						{auth.isLoggedIn && !isHome ? (
							<Link
								to="/wallet"
								className="create-composer-wallet mr-auto hidden items-center gap-1 text-xs font-bold text-white/45 transition hover:text-white sm:flex"
							>
								<Gem size={14} />
								{wallet.freeRemaining > 0
									? t("createGame.freeLeft", { count: wallet.freeRemaining })
									: wallet.creditsBalance}
							</Link>
						) : null}
						<div ref={attachmentMenuRef} className="relative">
							<button
								type="button"
								onClick={() => setAttachmentMenuOpen((open) => !open)}
								disabled={submitting}
								aria-label={t("createGame.attachmentsLabel")}
								aria-haspopup="menu"
								aria-expanded={attachmentMenuOpen}
								className="create-composer-tool grid size-10 place-items-center rounded-full bg-white/[0.07] text-white/65 transition hover:bg-white/[0.12] hover:text-white disabled:opacity-35"
							>
								<Paperclip size={19} />
							</button>
							{attachmentMenuOpen ? (
								<div
									role="menu"
									className="create-composer-menu absolute bottom-full right-0 z-30 mb-2 w-44 overflow-hidden rounded-lg border border-white/15 bg-[#201b27] p-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.55)]"
								>
									<AttachmentMenuItem
										icon={ImageIcon}
										label={t("createGame.attachImage")}
										onClick={() => {
											setAttachmentMenuOpen(false);
											imageInputRef.current?.click();
										}}
									/>
									<AttachmentMenuItem
										icon={Mic}
										label={t("createGame.attachAudio")}
										onClick={() => {
											setAttachmentMenuOpen(false);
											audioInputRef.current?.click();
										}}
									/>
								</div>
							) : null}
						</div>
						<button
							type="button"
							onClick={() => void submit()}
							disabled={submitting || !submitReady}
							className={`create-composer-submit grid size-10 shrink-0 place-items-center rounded-full transition ${
								submitReady
									? "create-composer-submit-ready bg-white text-black shadow-[0_8px_22px_-12px_rgba(255,255,255,0.8)] hover:bg-white/90"
									: "bg-white/15 text-white/30"
							}`}
							aria-label={t("createGame.generate")}
						>
							{submitting ? <Spinner size={17} /> : <ArrowUp size={20} />}
						</button>
					</div>
				</div>

				{error ? (
					<div className="create-composer-error mt-3 flex items-center justify-between gap-3 rounded-md border border-danger/35 bg-danger/10 px-3 py-2 text-sm text-danger">
						<span>{error}</span>
						{!canGenerate ? (
							<Link to="/wallet" className="font-bold underline underline-offset-2 hover:text-white">
								{t("createGame.openWallet")}
							</Link>
						) : null}
					</div>
				) : null}
			</div>
		);
	},
);

function AttachmentMenuItem({
	icon: Icon,
	label,
	onClick,
}: {
	icon: typeof ImageIcon;
	label: string;
	onClick: () => void;
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			role="menuitem"
		className="create-composer-menu-item flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm font-semibold text-white/70 transition hover:bg-white/[0.08] hover:text-white"
		>
			<Icon size={17} />
			<span>{label}</span>
		</button>
	);
}
